/**
 * StatefulConnectionsSection component for displaying stateful-TCP egress connection audit
 * events (e.g. TDS/Azure SQL, PostgreSQL, Redis). Unlike the HTTP-oriented ConnectionsSection,
 * these are long-lived connections with a lifecycle: an "Established" event when the connection
 * opens and a "Closed" event when it ends. The two phases share a correlation id, so entries are
 * grouped into a single tile per connection that shows whether it is currently open or closed.
 *
 * @module components/connectors/StatefulConnectionsSection
 */

import { useMemo } from 'react';
import { Database, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Card, CardHeader, CardBody, Badge } from '@/components/ui';
import type { StatefulTcpEgressEntry } from '@/types';
import { isRedirectGatewayArtifact, toMs } from './unifiedEgress';

type ConnectionStatus = 'open' | 'closed' | 'failed';

interface TcpConnectionStats {
  /** Grouping key: correlationId or synthetic per-entry key. */
  id: string;
  /** Friendly connector label (e.g. "Azure SQL"). */
  connectorLabel: string;
  /** Target endpoint label, e.g. "server:port". */
  endpoint: string;
  /** Target database, if reported. */
  database?: string;
  /** Proxy login name (already masked by the API). */
  proxyLoginName?: string;
  /** Sandbox source IP. */
  sourceIp?: string;
  /** Derived connection status. */
  status: ConnectionStatus;
  /** Whether a Closed phase exists, meaning byte/duration metrics are final. */
  metricsAreFinal: boolean;
  /** Outcome string (Connected, PolicyDenied, ...). */
  outcome?: string;
  /** Failure detail when the outcome is not Connected. */
  failureReason?: string;
  /** Bytes received from the sandbox (final on Closed). */
  bytesIn?: number;
  /** Bytes sent to the backend (final on Closed). */
  bytesOut?: number;
  /** Elapsed time to the terminal phase, in ms. */
  durationMs?: number;
  /** When the connection was accepted. */
  startedAt?: Date;
  /** Most recent phase timestamp (for sorting). */
  lastActivity: Date;
}

/**
 * Maps a raw connector type to a friendly display label.
 */
function connectorLabel(connectorType?: string): string {
  switch (connectorType) {
    case 'AzureSql':
      return 'Azure SQL';
    case 'PostgreSql':
      return 'PostgreSQL';
    case 'Redis':
      return 'Redis';
    default:
      return connectorType || 'Connection';
  }
}

/**
 * Formats a byte count for compact display.
 */
function formatBytes(bytes?: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exp);
  return `${exp === 0 ? value : value.toFixed(1)} ${units[exp]}`;
}

/**
 * Formats an elapsed-milliseconds value for display.
 */
function formatDuration(ms?: number): string {
  if (!ms || ms <= 0) return '0 ms';
  if (ms < 1000) return `${ms} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  // Round to whole seconds *before* splitting so we never render an impossible
  // 60-second remainder (e.g. 119.9s must display as "2m 0s", not "1m 60s").
  const totalSeconds = Math.round(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const rem = totalSeconds % 60;
  return `${minutes}m ${rem}s`;
}

/**
 * Treats any non-"Connected" outcome as a failure.
 */
function isFailureOutcome(outcome?: string): boolean {
  return !!outcome && outcome.toLowerCase() !== 'connected';
}

/**
 * Groups raw phase entries into one record per connection (paired by correlationId) and derives
 * the connection status. Established + Closed entries for the same connection collapse into a
 * single tile. Byte/duration metrics are only final once a Closed phase exists; `metricsAreFinal`
 * captures that independently of success/failure so terminal-but-failed connections still surface
 * their final metrics.
 */
function aggregateConnections(entries: StatefulTcpEgressEntry[]): TcpConnectionStats[] {
  const groups = new Map<string, StatefulTcpEgressEntry[]>();

  entries.forEach((entry, index) => {
    // Correlation id pairs the two phases; fall back to a synthetic key so uncorrelated
    // entries still render as their own tile rather than colliding.
    const key = entry.correlationId || `entry-${index}`;
    const bucket = groups.get(key);
    if (bucket) bucket.push(entry);
    else groups.set(key, [entry]);
  });

  const stats: TcpConnectionStats[] = [];

  for (const [id, group] of groups) {
    const established = group.find((e) => e.phase === 'Established');
    const closed = group.find((e) => e.phase === 'Closed');
    const representative = closed ?? established ?? group[0];
    // Metrics (bytes/duration) are only final once the connection has closed. This is
    // orthogonal to success/failure: a connection can fail *after* transferring data, and we
    // still want to show those final metrics.
    const metricsAreFinal = closed !== undefined;
    const metricsSource = closed ?? representative;

    const outcome = representative.outcome;

    // Consolidate the Azure SQL Redirect gateway artifact identically to the unified audit view
    // (shared detector), so the same audit entries never render differently across the two surfaces.
    if (
      isRedirectGatewayArtifact({
        hasEstablished: !!established,
        hasClosed: !!closed,
        outcome,
        bytesIn: metricsSource.bytesIn ?? 0,
        bytesOut: metricsSource.bytesOut ?? 0,
      })
    ) {
      continue;
    }

    let status: ConnectionStatus;
    if (isFailureOutcome(outcome)) status = 'failed';
    else if (closed) status = 'closed';
    else if (established) status = 'open';
    else status = 'closed';

    const server = representative.server;
    const port = representative.port;
    const endpoint = server
      ? port
        ? `${server}:${port}`
        : server
      : connectorLabel(representative.connectorType);

    const startedAt = representative.startedAt ? new Date(representative.startedAt) : undefined;
    // Compare parsed epoch ms (not raw ISO strings) so mixed offsets/formats order correctly —
    // same approach as the unified audit view's aggregateConnections.
    const lastActivity = new Date(
      group.reduce((latest, e) => Math.max(latest, toMs(e.timestamp)), 0)
    );

    stats.push({
      id,
      connectorLabel: connectorLabel(representative.connectorType),
      endpoint,
      database: representative.database,
      proxyLoginName: representative.proxyLoginName,
      sourceIp: representative.sourceIp,
      status,
      metricsAreFinal,
      outcome,
      failureReason: representative.failureReason,
      bytesIn: metricsSource.bytesIn,
      bytesOut: metricsSource.bytesOut,
      durationMs: metricsSource.durationMs,
      startedAt,
      lastActivity,
    });
  }

  // Most recent activity first.
  return stats.sort((a, b) => b.lastActivity.getTime() - a.lastActivity.getTime());
}

/**
 * Renders the status badge for a connection.
 */
function StatusBadge({ stats }: { stats: TcpConnectionStats }) {
  if (stats.status === 'failed') {
    return <Badge variant="danger">{stats.outcome || 'Failed'}</Badge>;
  }
  if (stats.status === 'open') {
    return <Badge variant="success">Open</Badge>;
  }
  return <Badge variant="neutral">Closed</Badge>;
}

/**
 * The inner content of the stateful connections section.
 */
function StatefulConnectionsContent({
  connections,
  isLoading,
}: {
  connections: TcpConnectionStats[];
  isLoading?: boolean;
}) {
  if (isLoading) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Loading connection data...</p>;
  }

  if (connections.length === 0) {
    return (
      <div className="text-center py-6">
        <Database className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No database connections detected.
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-400 mt-1">
          TDS, PostgreSQL, and Redis connections will appear here when your app connects through a
          configured connector.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {connections.map((stats) => (
        <div
          key={stats.id}
          className="relative p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
        >
          {/* Endpoint + status */}
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <Database className="h-4 w-4 text-primary-500 shrink-0" />
              <span
                className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate"
                title={stats.endpoint}
              >
                {stats.endpoint}
              </span>
            </div>
            <StatusBadge stats={stats} />
          </div>

          {/* Metadata */}
          <div className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center justify-between gap-2">
              <span>Connector</span>
              <span
                className="text-gray-700 dark:text-gray-300 font-medium truncate"
                title={stats.connectorLabel}
              >
                {stats.connectorLabel}
              </span>
            </div>
            {stats.database && (
              <div className="flex items-center justify-between gap-2">
                <span>Database</span>
                <span className="text-gray-700 dark:text-gray-300 truncate" title={stats.database}>
                  {stats.database}
                </span>
              </div>
            )}
            {stats.proxyLoginName && (
              <div className="flex items-center justify-between gap-2">
                <span>Login</span>
                <span
                  className="text-gray-700 dark:text-gray-300 truncate"
                  title={stats.proxyLoginName}
                >
                  {stats.proxyLoginName}
                </span>
              </div>
            )}
            {stats.metricsAreFinal && (
              <div className="flex items-center justify-between gap-2">
                <span>Duration</span>
                <span className="text-gray-700 dark:text-gray-300">
                  {formatDuration(stats.durationMs)}
                </span>
              </div>
            )}
            {stats.failureReason && stats.status === 'failed' && (
              <div className="flex items-start justify-between gap-2">
                <span className="shrink-0">Reason</span>
                <span
                  className="text-danger-600 dark:text-danger-400 text-right wrap-break-word"
                  title={stats.failureReason}
                >
                  {stats.failureReason}
                </span>
              </div>
            )}
          </div>

          {/* Bytes — only final (and meaningful) once the connection has closed. */}
          {stats.metricsAreFinal && ((stats.bytesIn ?? 0) > 0 || (stats.bytesOut ?? 0) > 0) && (
            <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700 flex items-center gap-4">
              <div className="flex items-center gap-1 text-xs">
                <ArrowUpRight className="h-3 w-3 text-primary-500" />
                <span className="text-gray-700 dark:text-gray-300 font-medium">
                  {formatBytes(stats.bytesOut)}
                </span>
                <span className="text-gray-500 dark:text-gray-400">out</span>
              </div>
              <div className="flex items-center gap-1 text-xs">
                <ArrowDownRight className="h-3 w-3 text-primary-500" />
                <span className="text-gray-700 dark:text-gray-300 font-medium">
                  {formatBytes(stats.bytesIn)}
                </span>
                <span className="text-gray-500 dark:text-gray-400">in</span>
              </div>
            </div>
          )}

          {/* Started at */}
          {stats.startedAt && (
            <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
              <span className="text-xs text-gray-400 dark:text-gray-400">
                Started: {stats.startedAt.toLocaleTimeString()}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

interface StatefulConnectionsSectionProps {
  /** Stateful-TCP connection audit entries (one per lifecycle phase). */
  entries: StatefulTcpEgressEntry[];
  /** Whether data is currently loading. */
  isLoading?: boolean;
  /** Whether to render without Card wrapper (for use in panels/sections). */
  inline?: boolean;
}

/**
 * Displays stateful-TCP egress connections (Azure SQL / PostgreSQL / Redis) grouped by connection,
 * surfacing whether each is currently open or closed.
 */
export function StatefulConnectionsSection({
  entries,
  isLoading,
  inline = false,
}: StatefulConnectionsSectionProps) {
  const connections = useMemo(() => aggregateConnections(entries), [entries]);

  // Inline mode: render without Card wrapper (for use in panels/sections).
  if (inline) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-primary-500" />
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
              Database Connections
            </h4>
          </div>
          {connections.length > 0 && <Badge variant="info">{connections.length}</Badge>}
        </div>
        <StatefulConnectionsContent connections={connections} isLoading={isLoading} />
      </div>
    );
  }

  // Card mode: render with Card wrapper (for use in grid layouts).
  return (
    <Card className="lg:col-span-2">
      <CardHeader
        title="Database Connections"
        icon={<Database className="h-5 w-5 text-primary-500" />}
        action={
          connections.length > 0 ? <Badge variant="info">{connections.length}</Badge> : undefined
        }
      />
      <CardBody>
        <StatefulConnectionsContent connections={connections} isLoading={isLoading} />
      </CardBody>
    </Card>
  );
}
